import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';

export enum TMedicalExamStatus {
  FATURADO = 'FATURADO',
  POR_VALIDAR = 'POR VALIDAR',
}

export type TMedicalExam = {
  id: string;
  emissionDate: Date;
  expiryDate: Date;
  nFat: string;
  status: TMedicalExamStatus;
  officialProfessionalCategoryPortal?: {
    id?: string;
    name?: string;
  };
  professionalCategory?: {
    id?: string;
    portalId?: string;
    name?: string;
  };
  provider: {
    portalId: string;
    name: string;
  };
  costCenter: {
    portalId: string;
    reference: string;
    description: string;
    company:
      | {
          portalId: string;
          acronym: string;
        }
      | string;
    agency: {
      portalId: string;
      name: string;
      acronym: string;
    };
  };
  employee: {
    id: string;
    portalId: string;
    code: string;
    name: string;
    anonymized?: boolean;
  };
  attachmentRef: {
    fileId: string;
    fileName: string;
    storageKey: string;
    uploadedBy: {
      id: string;
      name: string;
      acronym: string;
      agency: {
        id: string;
        name: string;
        acronym: string;
      };
    };
  };
  createdAt: Date;
  updatedAt: Date;
};

export const medicalExamsApi = createApi({
  reducerPath: 'medicalExams',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.GATSBY_MEDICAL_EXAMS_API_URL,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['MedicalExams'],
  endpoints: (builder) => ({
    createMedicalExam: builder.mutation<
      Response<TMedicalExam>,
      { medicalExam: any }
    >({
      query: ({ medicalExam }) => {
        const body = new FormData();
        const file = medicalExam.medicalExamFile?.[0]?.file;
        body.append('medicalExamFile', file);
        body.append('emissionDate', medicalExam.emissionDate);
        body.append('expiryDate', medicalExam.expiryDate);
        body.append(
          'officialProfessionalCategoryPortal',
          JSON.stringify(medicalExam.officialProfessionalCategoryPortal ?? {}),
        );
        body.append('providerId', medicalExam.providerId);
        body.append('costCenterId', medicalExam.costCenterId);
        body.append('employee', medicalExam.employee);
        body.append('originalName', file?.name);
        return {
          url: '',
          method: 'POST',
          body,
        };
      },
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    getMedicalExams: builder.query<Response<TMedicalExam>, TQueryParams>({
      providesTags: ['MedicalExams'],
      query: (params) => formatQueryParams(params),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TMedicalExam>(
          res as JsonApiResponseDto<TMedicalExam>,
          meta,
          args,
          true,
        );
      },
    }),
    exportMedicalExams: builder.mutation({
      queryFn: async (params, api, extraOptions, baseQuery) => {
        const result: any = await baseQuery({
          url: `export/csv${formatQueryParams(params)}`,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);

          hiddenElement.href = blobPDF;
          hiddenElement.download = 'exames-medicos.csv';

          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    getMedicalExam: builder.query<
      Response<TMedicalExam>,
      TQueryParams & { medicalExamId: string }
    >({
      providesTags: ['MedicalExams'],
      query: ({ medicalExamId, populate }) => {
        return formatQueryParams({ populate }, medicalExamId);
      },
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    updateMedicalExam: builder.mutation<
      Response<TMedicalExam>,
      {
        id: string;
        medicalExam: any;
      }
    >({
      invalidatesTags: ['MedicalExams'],
      query: ({ id, medicalExam }) => ({
        url: id,
        method: 'PUT',
        body: {
          ...medicalExam,
        },
      }),
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    deleteMedicalExam: builder.mutation<Response<TMedicalExam>, { id: string }>(
      {
        invalidatesTags: ['MedicalExams'],
        query: ({ id }) => ({
          url: id,
          method: 'DELETE',
        }),
        transformResponse: parseJsonApiResponse<TMedicalExam>,
      },
    ),
    updateMedicalExamDocument: builder.mutation<
      Response<TMedicalExam>,
      {
        id: string;
        document: any;
      }
    >({
      invalidatesTags: ['MedicalExams'],
      query: ({ id, document }) => {
        const body = new FormData();
        const file = document[0]?.file;
        body.append('medicalExamFile', file);
        body.append('originalName', file.name);
        return {
          url: `${id}/document`,
          method: 'PUT',
          body,
        };
      },
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    deleteEmployeeMedicalExamDocument: builder.mutation<
      Response<TMedicalExam>,
      { id: string; documentId: string }
    >({
      invalidatesTags: ['MedicalExams'],
      query: ({ id, documentId }) => ({
        url: `${id}/document/${documentId}`,
        method: 'DELETE',
      }),
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    editMedicalExamsStatusAndInvoiceNumber: builder.mutation<
      Response<TMedicalExam>,
      {
        body: {
          ids: string[];
          status?: string;
          nFat?: string;
        };
      }
    >({
      invalidatesTags: ['MedicalExams'],
      query: ({ body }) => ({
        url: 'bulk/update-status-and-nfat',
        method: 'PUT',
        body,
      }),
      transformResponse: parseJsonApiResponse<TMedicalExam>,
    }),
    getMedicalExamDocument: builder.mutation({
      queryFn: async (
        { medicalExamId, documentId },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `${medicalExamId}/document/${documentId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);

          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';

          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
  }),
});

export const {
  useCreateMedicalExamMutation,
  useGetMedicalExamsQuery,
  useDeleteMedicalExamMutation,
  useExportMedicalExamsMutation,
  useGetMedicalExamQuery,
  useUpdateMedicalExamMutation,
  useUpdateMedicalExamDocumentMutation,
  useDeleteEmployeeMedicalExamDocumentMutation,
  useEditMedicalExamsStatusAndInvoiceNumberMutation,
  useGetMedicalExamDocumentMutation,
} = medicalExamsApi;
